// Editör için ses erişimi atama modal'ı.
// Master-only; set_user_voices RPC'si audit'i kendi yazıyor.

import { AlertTriangle, Loader2, X } from 'lucide-react';
import { type AdminUser, type VoiceRow } from '../lib/supabase';

interface Props {
  user: AdminUser;
  allVoices: VoiceRow[];
  selectedSlugs: Set<string>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onToggle: (slug: string) => void;
  onSave: () => void;
  onClose: () => void;
}

export default function VoiceAccessModal({
  user,
  allVoices,
  selectedSlugs,
  loading,
  saving,
  error,
  onToggle,
  onSave,
  onClose,
}: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-slate-800">
              {user.display_name ?? user.email} — Ses Erişimi
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-1.5">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>
              <strong>Hiç seçili değilse bu editör HİÇBİR sesi kullanamaz.</strong>
            </span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 size={14} className="animate-spin" /> Yükleniyor…
            </div>
          ) : allVoices.length === 0 ? (
            <p className="text-sm text-slate-500">Aktif ses profili bulunamadı.</p>
          ) : (
            <div className="space-y-2">
              {allVoices.map((vp) => (
                <label
                  key={vp.slug}
                  className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 select-none"
                >
                  <input
                    type="checkbox"
                    checked={selectedSlugs.has(vp.slug)}
                    onChange={() => onToggle(vp.slug)}
                    className="h-4 w-4 rounded border-slate-300 text-[color:var(--color-primary)] focus:ring-[color:var(--color-primary)]"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{vp.display_name}</p>
                    {vp.category && (
                      <p className="text-xs text-slate-400">{vp.category}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-40"
          >
            Vazgeç
          </button>
          <button
            onClick={onSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-soft)] text-white rounded-lg text-sm font-medium disabled:opacity-40"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
